/**
 * Coordinate Parser Utility
 *
 * Pure functions for parsing and validating geographic coordinates in various formats:
 * - Decimal Degrees (DD): -23.5505, -46.6333
 * - Degrees Minutes Seconds (DMS): 23°33'01.8"S 46°37'59.9"W
 * - Degrees Decimal Minutes (DMM): 23°33.030'S 46°37.998'W
 */

const MIN_LAT = -90
const MAX_LAT = 90
const MIN_LNG = -180
const MAX_LNG = 180

/**
 * Validate latitude is within valid range
 */
export function isValidLatitude(lat) {
  return lat >= MIN_LAT && lat <= MAX_LAT
}

/**
 * Validate longitude is within valid range
 */
export function isValidLongitude(lng) {
  return lng >= MIN_LNG && lng <= MAX_LNG
}

/**
 * Round coordinate value to six decimal places (~0.11m precision)
 */
export function roundCoordinate(value) {
  return Math.round(value * 1000000) / 1000000
}

/**
 * Format coordinates as DD string for display
 */
export function formatDD(lat, lng) {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`
}

/**
 * Parse Decimal Degrees format
 * Examples: -23.5505, -46.6333 or -23.5505 -46.6333
 */
function parseDD(input) {
  const ddRegex = /^(-?\d+\.?\d*)\s*[,;]\s*(-?\d+\.?\d*)$/
  const match = input.match(ddRegex)

  if (match) {
    const lat = parseFloat(match[1])
    const lng = parseFloat(match[2])

    if (isValidLatitude(lat) && isValidLongitude(lng)) {
      return { lat, lng }
    }
  }

  return null
}

/**
 * Parse Degrees Minutes Seconds format
 * Examples: 23°33'01.8"S 46°37'59.9"W or 16° 24′ 21″ S, 49° 13′ 08″ O
 */
function parseDMS(input) {
  const dmsRegex = /(\d+)[°\s]+(\d+)[′']([\d.]+)[″"]\s*([NS])\s*[,;]?\s*(\d+)[°\s]+(\d+)[′']([\d.]+)[″"]\s*([EWO])/
  const match = input.match(dmsRegex)

  if (match) {
    const latDeg = parseInt(match[1], 10)
    const latMin = parseInt(match[2], 10)
    const latSec = parseFloat(match[3])
    const latDir = match[4]

    const lngDeg = parseInt(match[5], 10)
    const lngMin = parseInt(match[6], 10)
    const lngSec = parseFloat(match[7])
    const lngDir = match[8]

    let lat = latDeg + latMin / 60 + latSec / 3600
    let lng = lngDeg + lngMin / 60 + lngSec / 3600

    if (latDir === 'S') lat = -lat
    if (lngDir === 'W' || lngDir === 'O') lng = -lng

    if (isValidLatitude(lat) && isValidLongitude(lng)) {
      return { lat, lng }
    }
  }

  return null
}

/**
 * Parse Degrees Decimal Minutes format
 * Examples: 23°33.030'S 46°37.998'W or 16° 24.35′ S, 49° 13.13′ O
 */
function parseDMM(input) {
  const dmmRegex = /(\d+)[°\s]+([\d.]+)[′']\s*([NS])\s*[,;]?\s*(\d+)[°\s]+([\d.]+)[′']\s*([EWO])/
  const match = input.match(dmmRegex)

  if (match) {
    const latDeg = parseInt(match[1], 10)
    const latMin = parseFloat(match[2])
    const latDir = match[3]

    const lngDeg = parseInt(match[4], 10)
    const lngMin = parseFloat(match[5])
    const lngDir = match[6]

    let lat = latDeg + latMin / 60
    let lng = lngDeg + lngMin / 60

    if (latDir === 'S') lat = -lat
    if (lngDir === 'W' || lngDir === 'O') lng = -lng

    if (isValidLatitude(lat) && isValidLongitude(lng)) {
      return { lat, lng }
    }
  }

  return null
}

/**
 * Parse coordinate string in any supported format (DD, DMS, DMM)
 * Returns { lat: number, lng: number } or null if no format matches
 */
export function parseCoordinates(input) {
  if (!input || typeof input !== 'string') return null

  const normalized = input.replace(/\s+/g, ' ').trim()

  return parseDD(normalized) || parseDMS(normalized) || parseDMM(normalized)
}
